import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
// @ts-ignore
import * as HeatmapOverlay from 'heatmap.js/plugins/leaflet-heatmap/leaflet-heatmap';
import { useQuery } from '@apollo/client';
import {
    CAMPAIGN_RESULTS_QUERY,
    CAMPAIGN_VIS_MAP_QUERY,
} from 'data/ForecastingResults';
import NGMapContainer from 'leaflet/MapContainer';
import {
    FeatureGroup,
    useMap,
    useMapEvents,
    Tooltip,
    CircleMarker, Marker, Circle, Polygon
} from 'react-leaflet';
import { cellToLatLng, cellToBoundary } from 'h3-js';
import { DivIcon, LatLngBounds, LatLngTuple } from 'leaflet';
import { GeoQueryBounds, leafletBoundsToQueryVariables } from 'data/helpers';
import 'react-datepicker/dist/react-datepicker.css';
import SummaryStatistics from 'components/SummaryStatistics';
import ExportScreenList from 'components/ExportScreenList';
import 'routes/map.css';import {
    CampaignVisMap,
    CampaignVisMap_campaignVisMap,
    CampaignVisMapVariables
} from '../data/__generated__/CampaignVisMap';
import CampaignVisSettings, {
    useCampaignVisualisationSettings
} from './CampaignVisSettings';
import { CampaignVisResultsQuery } from '../data/__generated__/CampaignVisResultsQuery';
import { formatNumber } from '../leaflet/util/SummaryFormatting';

function CircleLayer(
    {
        flyDest,
        setFlyDest
    }: {
        flyDest: LatLngBounds | null,
        setFlyDest: React.Dispatch<React.SetStateAction<LatLngBounds | null>>
    }
) {
    const { campaignVisualisationSettings } = useCampaignVisualisationSettings();
    const map = useMap();
    const [bounds, setBounds] = useState(map.getBounds());
    const [zoomedAllIn, setZoomedAllIn] = useState(false);

    useMapEvents({
        zoom() {
            setBounds(map.getBounds());
            setZoomedAllIn(map.getMaxZoom() - map.getZoom() < 1.0);
        },
        moveend() {
            setBounds(map.getBounds());
            setFlyDest(null);
        }
    });

    const [queryBounds, setQueryBounds] = useState<GeoQueryBounds>(leafletBoundsToQueryVariables(bounds));
    const hexData = useQuery<CampaignVisMap, CampaignVisMapVariables>(CAMPAIGN_VIS_MAP_QUERY, {
        variables: {
            ...queryBounds,
            campaignId: campaignVisualisationSettings.campaignId!
        },
        skip: zoomedAllIn,
    });

    // commented out for now, but left in case pin data should be added back for campaign visualisation
    /*const pinData = useQuery<
        >(CAMPAIGN_VIS_MAP_QUERY, {
        variables: {
            ...queryBounds,
            campaignId: campaignVisualisationSettings.campaignId!,
        },
    });*/

    const loading = hexData.loading;
    const latestData = useMemo(() => hexData.data?.campaignVisMap, [hexData]);
    useEffect(() => {
        if (flyDest || !loading) {
            setQueryBounds(leafletBoundsToQueryVariables(flyDest ?? bounds));
        }
    }, [bounds, loading, flyDest]);

    const [dataOrPrevious, setDataOrPrevious] = useState(latestData ?? []);
    useEffect(() => {
        // creating new hexes on the screen while in 'fly' mode doesn't work, so we only update once it lands
        if (latestData && latestData !== dataOrPrevious && flyDest === null) {
            setDataOrPrevious(latestData);
        }
    }, [dataOrPrevious, latestData, flyDest]);
    useEffect(() => {
        setDataOrPrevious(latestData ?? []);
    }, [campaignVisualisationSettings.campaignId]);
    const displayHexes = useMemo(() => {
        return dataOrPrevious.flatMap(({ h3, data }) => {
            const center = cellToLatLng(h3);
            const boundary = cellToBoundary(h3);
            if (center[1] < -160 || center[1] > 160) {
                // Those that appear near the meridian get drawn on both sides.
                // If the viewport is zoomed in to one side, then the other side just won't show, but it will be there.
                const posVersion = {
                    center: center[1] > 0 ? center : [center[0], center[1] + 360] as [number, number],
                    boundary: boundary.map(it => it[1] > 0 ? it : [it[0], it[1] + 360]) as [number, number][],
                    data,
                    h3,
                    projection: 'pos'
                };
                const negVersion = {
                    center: center[1] < 0 ? center : [center[0], center[1] - 360] as [number, number],
                    boundary: boundary.map(it => it[1] < 0 ? it : [it[0], it[1] - 360]) as [number, number][],
                    data,
                    h3,
                    projection: 'neg'
                };
                return [posVersion, negVersion];
            }
            return [{
                center,
                boundary,
                data,
                h3,
                projection: ''
            }];
        }).map(({ h3, data, boundary, center, projection }) => {
            const count: number = data.impressions ?? 0;
            const icon = new DivIcon({
                html: `<p>${count.toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                })}</p>`,
            });
            const onClick = () => {
                const zoom = map.getBoundsZoom(boundary);
                // @ts-ignore
                const eventualPixelBounds = map.getPixelBounds(center, zoom);
                setFlyDest(new LatLngBounds(
                    map.unproject(eventualPixelBounds.getBottomLeft(), zoom),
                    map.unproject(eventualPixelBounds.getTopRight(), zoom)
                ));
                map.flyTo(center, zoom, { easeLinearity: 1, animate: true });
            };
            const colour = ((it: number) => {
                if (it < 10) return '#F7D031';
                else if (it < 100) return '#0099FA';
                else return '#002F87';
            })(count);
            return <div key={`${h3}-div-${projection}`}>
                <Marker
                    key={`${h3}-label-${projection}`}
                    position={center}
                    icon={icon}
                    eventHandlers={{ click: onClick }}
                />
                <Polygon
                    key={`${h3}-${colour}`}
                    positions={boundary}
                    fillColor={colour}
                    color={colour}
                    weight={1}
                    opacity={1}
                    fillOpacity={0.6}
                    eventHandlers={{ click: onClick }}
                />
            </div>;
        });
    }, [dataOrPrevious]);


    return (
        <>
            <FeatureGroup key={'featureGroup'}>
                {displayHexes}
            </FeatureGroup>
        </>
    );
}

export default function SampledAvails() {
    const startingPoint: LatLngTuple = [53.68, -2.62];
    const controlsRef = useRef();
    const [flyDest, setFlyDest] = useState<LatLngBounds | null>(null);

    return (
        <div className='relative h-full flex-grow flex'>
            <div className='leaflet-top leaflet-left'>
                {/*<ForecastingPanel />*/}
            </div>
            <NGMapContainer
                center={startingPoint}
                defaultZoom={6}
                controlsRef={controlsRef}
            >
                <CircleLayer flyDest={flyDest} setFlyDest={setFlyDest} />
                <CampaignVisSettings setFlyDest={setFlyDest}/>
            </NGMapContainer>
        </div>
    );
}